import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from "firebase/auth";
import { auth } from "../firebaseConfig";

const shellStyle = {
  minHeight: "100vh",
  background:
    "radial-gradient(circle at top, rgba(20,115,180,0.28), transparent 35%), linear-gradient(180deg, #03101f 0%, #020b18 100%)",
  color: "#f8fafc",
  display: "flex",
  flexDirection: "column",
};

const headerStyle = {
  width: "100%",
  maxWidth: 1200,
  margin: "0 auto",
  padding: "22px 24px 10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  boxSizing: "border-box",
};

const brandWrapStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  color: "inherit",
  textDecoration: "none",
};

const logoStyle = {
  minWidth: 46,
  height: 30,
  borderRadius: 999,
  background: "#20c7f3",
  color: "#03101f",
  display: "grid",
  placeItems: "center",
  fontWeight: 800,
  fontSize: 16,
};

const cardWrapStyle = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  boxSizing: "border-box",
};

const cardStyle = {
  width: "100%",
  maxWidth: 560,
  borderRadius: 28,
  border: "1px solid rgba(125, 211, 252, 0.22)",
  background: "rgba(3, 16, 31, 0.84)",
  boxShadow: "0 28px 80px rgba(0, 0, 0, 0.38)",
  padding: 32,
  boxSizing: "border-box",
  backdropFilter: "blur(10px)",
};

const badgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  padding: "8px 14px",
  borderRadius: 999,
  border: "1px solid rgba(32, 199, 243, 0.35)",
  color: "#7dd3fc",
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
};

const titleStyle = {
  margin: "18px 0 10px",
  fontSize: "clamp(32px, 4vw, 44px)",
  lineHeight: 1.02,
  fontWeight: 800,
  letterSpacing: "-0.03em",
};

const subtitleStyle = {
  margin: 0,
  color: "rgba(226, 232, 240, 0.82)",
  lineHeight: 1.65,
  fontSize: 16,
};

const labelStyle = {
  display: "block",
  fontSize: 13,
  marginBottom: 8,
  color: "#cbd5e1",
  fontWeight: 700,
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  background: "rgba(15, 23, 42, 0.88)",
  border: "1px solid rgba(148, 163, 184, 0.24)",
  color: "#f8fafc",
  borderRadius: 14,
  padding: "14px 16px",
  outline: "none",
  fontSize: 15,
};

const buttonStyle = {
  width: "100%",
  border: 0,
  borderRadius: 16,
  padding: "15px 18px",
  fontSize: 16,
  fontWeight: 800,
  cursor: "pointer",
  background: "#20c7f3",
  color: "#03101f",
  marginTop: 18,
};

const secondaryButtonStyle = {
  ...buttonStyle,
  background: "transparent",
  color: "#f8fafc",
  border: "1px solid rgba(32, 199, 243, 0.38)",
};

function getActionState() {
  const params = new URLSearchParams(window.location.search);
  return {
    mode: params.get("mode") || "",
    oobCode: params.get("oobCode") || "",
    continueUrl: params.get("continueUrl") || "",
    lang: params.get("lang") || "en",
  };
}

function classifyActionError(err) {
  const code = err?.code || "";
  const message = String(err?.message || "").toLowerCase();

  if (
    code === "auth/expired-action-code" ||
    code === "auth/invalid-action-code" ||
    code === "auth/user-disabled" ||
    code === "auth/user-not-found"
  ) {
    return {
      type: "invalid",
      text: "This link is invalid or has expired. Please request a new one.",
    };
  }

  if (
    code === "auth/network-request-failed" ||
    code === "appCheck/fetch-status-error" ||
    message.includes("appcheck") ||
    message.includes("network") ||
    message.includes("failed to fetch") ||
    message.includes("fetch") ||
    message.includes("backend") ||
    message.includes("403") ||
    message.includes("400")
  ) {
    return {
      type: "temporary",
      text: "We could not verify this link right now. Please refresh and try again in a moment.",
    };
  }

  return {
    type: "generic",
    text: err?.message || "We could not process this request.",
  };
}

export default function PasswordActionPage() {
  const navigate = useNavigate();
  const { mode, oobCode } = useMemo(() => getActionState(), []);
  const [status, setStatus] = useState("loading");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let mounted = true;

    async function run() {
      if (!mode || !oobCode) {
        if (mounted) {
          setError("This action link is incomplete or invalid.");
          setStatus("error");
        }
        return;
      }

      try {
        if (mode === "resetPassword") {
          const resolvedEmail = await verifyPasswordResetCode(auth, oobCode);
          if (!mounted) return;
          setEmail(resolvedEmail || "");
          setError("");
          setStatus("ready");
          return;
        }

        if (mode === "verifyEmail") {
          await checkActionCode(auth, oobCode);
          if (!mounted) return;
          setError("");
          setStatus("verify-ready");
          return;
        }

        if (mounted) {
          setError("This action type is not supported on this page yet.");
          setStatus("error");
        }
      } catch (err) {
        if (!mounted) return;
        console.error("[PasswordActionPage] action validation failed:", err);
        const parsed = classifyActionError(err);
        setError(parsed.text);
        setStatus(parsed.type === "invalid" ? "error" : "temporary-error");
      }
    }

    run();
    return () => {
      mounted = false;
    };
  }, [mode, oobCode, attempt]);

  async function handleResetSubmit(e) {
    e.preventDefault();
    setError("");

    if (!password || password.length < 6) {
      setError("Your new password must be at least 6 characters long.");
      return;
    }

    if (password !== confirmPassword) {
      setError("The passwords do not match.");
      return;
    }

    try {
      setBusy(true);
      await confirmPasswordReset(auth, oobCode, password);
      setStatus("success");
    } catch (err) {
      console.error("[PasswordActionPage] reset submit failed:", err);
      const code = err?.code || "";
      if (code === "auth/weak-password") {
        setError("Please choose a stronger password.");
        setStatus("ready");
      } else {
        const parsed = classifyActionError(err);
        setError(parsed.text);
        setStatus(parsed.type === "invalid" ? "error" : "temporary-error");
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleVerifyEmail() {
    setError("");
    try {
      setBusy(true);
      await applyActionCode(auth, oobCode);
      setStatus("verified");
    } catch (err) {
      console.error("[PasswordActionPage] verify email failed:", err);
      const parsed = classifyActionError(err);
      setError(
        parsed.type === "invalid"
          ? "This verification link is invalid or has expired."
          : parsed.text
      );
      setStatus(parsed.type === "invalid" ? "error" : "temporary-error");
    } finally {
      setBusy(false);
    }
  }

  function retryCheck() {
    setError("");
    setStatus("loading");
    setAttempt((n) => n + 1);
  }

  function renderBody() {
    if (status === "loading") {
      return (
        <>
          <span style={badgeStyle}>Secure Account Action</span>
          <h1 style={titleStyle}>Checking your link…</h1>
          <p style={subtitleStyle}>
            Please wait while we validate your Neo Platform request.
          </p>
        </>
      );
    }

    if (status === "ready") {
      return (
        <>
          <span style={badgeStyle}>Neo Platform</span>
          <h1 style={titleStyle}>Create your new password</h1>
          <p style={subtitleStyle}>
            Set a secure password for <strong>{email}</strong> to continue into Neo Platform.
          </p>

          <form onSubmit={handleResetSubmit} style={{ marginTop: 24 }}>
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>New password</label>
              <input
                style={inputStyle}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                placeholder="Enter a new password"
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Confirm password</label>
              <input
                style={inputStyle}
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                placeholder="Confirm your new password"
              />
            </div>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontSize: 14,
                color: "#cbd5e1",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={showPassword}
                onChange={(e) => setShowPassword(e.target.checked)}
              />
              Show password
            </label>

            {error ? (
              <div
                style={{
                  marginTop: 16,
                  borderRadius: 14,
                  padding: "12px 14px",
                  background: "rgba(127, 29, 29, 0.24)",
                  border: "1px solid rgba(248, 113, 113, 0.32)",
                  color: "#fecaca",
                  fontSize: 14,
                  lineHeight: 1.5,
                }}
              >
                {error}
              </div>
            ) : null}

            <button type="submit" style={buttonStyle} disabled={busy}>
              {busy ? "Updating password…" : "Save new password"}
            </button>
          </form>
        </>
      );
    }

    if (status === "success") {
      return (
        <>
          <span style={badgeStyle}>Password Updated</span>
          <h1 style={titleStyle}>You’re all set</h1>
          <p style={subtitleStyle}>
            Your Neo Platform password has been updated successfully. You can now sign in with your new password.
          </p>
          <button style={buttonStyle} onClick={() => { window.location.href = "/login"; }}>Go to sign in</button>
        </>
      );
    }

    if (status === "verify-ready") {
      return (
        <>
          <span style={badgeStyle}>Email Verification</span>
          <h1 style={titleStyle}>Verify your email address</h1>
          <p style={subtitleStyle}>
            Confirm this email address to activate access to Neo Platform.
          </p>
          {error ? (
            <div
              style={{
                marginTop: 16,
                borderRadius: 14,
                padding: "12px 14px",
                background: "rgba(127, 29, 29, 0.24)",
                border: "1px solid rgba(248, 113, 113, 0.32)",
                color: "#fecaca",
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              {error}
            </div>
          ) : null}
          <button style={buttonStyle} onClick={handleVerifyEmail} disabled={busy}>
            {busy ? "Verifying…" : "Verify email"}
          </button>
        </>
      );
    }

    if (status === "verified") {
      return (
        <>
          <span style={badgeStyle}>Email Verified</span>
          <h1 style={titleStyle}>Verification complete</h1>
          <p style={subtitleStyle}>
            Your email has been verified successfully. You can continue to sign in.
          </p>
          <button style={buttonStyle} onClick={() => { window.location.href = "/login"; }}>Go to sign in</button>
        </>
      );
    }

    if (status === "temporary-error") {
      return (
        <>
          <span style={badgeStyle}>Temporary Verification Issue</span>
          <h1 style={titleStyle}>We couldn’t verify this link right now</h1>
          <p style={subtitleStyle}>
            {error || "Please refresh and try again in a moment. Your link may still be valid."}
          </p>
          <div style={{ display: "flex", gap: 12, marginTop: 22, flexWrap: "wrap" }}>
            <button style={buttonStyle} onClick={retryCheck}>Try again</button>
            <Link
              to="/forgot-password"
              style={{
                ...secondaryButtonStyle,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Request new link
            </Link>
          </div>
        </>
      );
    }

    return (
      <>
        <span style={badgeStyle}>Action Unavailable</span>
        <h1 style={titleStyle}>We couldn’t open this link</h1>
        <p style={subtitleStyle}>
          {error || "This secure action link is invalid, unsupported, or expired."}
        </p>
        <div style={{ display: "flex", gap: 12, marginTop: 22, flexWrap: "wrap" }}>
          <button style={buttonStyle} onClick={() => { window.location.href = "/login"; }}>Back to sign in</button>
          <Link
            to="/"
            style={{
              ...secondaryButtonStyle,
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Go to website
          </Link>
        </div>
      </>
    );
  }

  return (
    <div style={shellStyle}>
      <div style={headerStyle}>
        <Link to="/" style={brandWrapStyle}>
          <div style={logoStyle}>IM</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 18 }}>MatrixOS</div>
            <div style={{ color: "#67e8f9", fontSize: 12, fontWeight: 700 }}>
              Operations Intelligence
            </div>
          </div>
        </Link>
      </div>

      <div style={cardWrapStyle}>
        <div style={cardStyle}>{renderBody()}</div>
      </div>
    </div>
  );
}
